/**
 * AutoAssignProgressDialog
 *
 * Shows a real-time, phase-by-phase progress animation while the auto-assign
 * engine runs. All data displayed is real — derived from actual engine callbacks.
 *
 * Phases:
 *   1. discovery   — periods found for the absent teacher
 *   2. availability — teacher pool filtered by all 8 eligibility rules
 *   3. matching    — per-period cards updated as each match is made
 *   4. complete    — summary dashboard with final stats
 *   5. skipped     — engine skipped (cancelled, draft_exists, holiday, etc.)
 *   6. error       — engine threw an error
 *
 * Fast-operation guarantee:
 *   Each phase is displayed for a minimum of MIN_PHASE_MS (600ms) so the user
 *   can read the result even when the real work completes instantly. This is
 *   honest — we're not faking work, just ensuring legibility.
 *
 * Bulk mode:
 *   When processing multiple absences (Auto-Assign All), the dialog shows a
 *   teacher queue at the top and updates the progress panel for each teacher
 *   in sequence.
 */

import { useEffect, useRef, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  LinearProgress,
  Chip,
  Avatar,
  Button,
  Divider,
  CircularProgress,
  Collapse,
  Alert,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import SearchIcon from '@mui/icons-material/Search';
import GroupsIcon from '@mui/icons-material/Groups';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import SkipNextIcon from '@mui/icons-material/SkipNext';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import PersonIcon from '@mui/icons-material/Person';
import type { AutoAssignProgress, AutoAssignPhase, PeriodProgressState } from '@timetable/types';

// ─── Constants ────────────────────────────────────────────────────────────────

/** Minimum ms each phase is shown — ensures legibility even for instant operations */
const MIN_PHASE_MS = 600;

// ─── Phase step config ────────────────────────────────────────────────────────

interface PhaseStep {
  phase: AutoAssignPhase | 'discovery' | 'availability' | 'matching' | 'complete';
  label: string;
  icon: React.ReactNode;
}

const PHASE_STEPS: PhaseStep[] = [
  { phase: 'discovery',    label: 'Schedule Check',  icon: <SearchIcon sx={{ fontSize: 16 }} /> },
  { phase: 'availability', label: 'Teacher Pool',    icon: <GroupsIcon sx={{ fontSize: 16 }} /> },
  { phase: 'matching',     label: 'Smart Matching',  icon: <AutoFixHighIcon sx={{ fontSize: 16 }} /> },
  { phase: 'complete',     label: 'Done',            icon: <EmojiEventsIcon sx={{ fontSize: 16 }} /> },
];

const PHASE_ORDER: AutoAssignPhase[] = ['discovery', 'availability', 'matching', 'complete'];

function phaseIndex(phase: AutoAssignPhase): number {
  return PHASE_ORDER.indexOf(phase);
}

// ─── Bulk teacher queue item ──────────────────────────────────────────────────

export interface BulkQueueItem {
  absenceId: string;
  teacherName: string;
  status: 'pending' | 'running' | 'done' | 'skipped' | 'error';
  periodsAssigned?: number;
  periodsTotal?: number;
  skippedReason?: string;
}

// ─── Props ────────────────────────────────────────────────────────────────────

export interface AutoAssignProgressDialogProps {
  open: boolean;
  /** Current progress state from the engine callback */
  progress: AutoAssignProgress | null;
  /** For bulk mode — list of all absences being processed */
  bulkQueue?: BulkQueueItem[];
  /** Called when user closes the dialog (only enabled when engine is done) */
  onClose: () => void;
  /** Whether the engine is still running */
  isRunning: boolean;
}

// ─── Period status card ───────────────────────────────────────────────────────

function PeriodCard({ state }: { state: PeriodProgressState }) {
  const isSearching = state.status === 'searching';
  const isAssigned = state.status === 'assigned';
  const isUnassigned = state.status === 'unassigned';
  const isPending = state.status === 'pending';

  let borderColor = 'divider';
  let bgColor = 'background.paper';
  if (isAssigned)   { borderColor = '#0F766E'; bgColor = '#F0FDFA'; }
  if (isUnassigned) { borderColor = '#92400E'; bgColor = '#FFFBEB'; }
  if (isSearching)  { borderColor = 'primary.main'; bgColor = 'action.hover'; }

  return (
    <Box
      sx={{
        border: '1px solid',
        borderColor,
        borderRadius: 1.5,
        p: 1.5,
        bgcolor: bgColor,
        minWidth: 200,
        flexShrink: 0,
        transition: 'all 0.25s ease',
      }}
    >
      {/* Period header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.75 }}>
        <Typography variant="caption" fontWeight="bold" color="text.secondary">
          P{state.periodNumber} · {state.timeStart.slice(0, 5)}–{state.timeEnd.slice(0, 5)}
        </Typography>
        {isSearching && <CircularProgress size={12} />}
        {isAssigned && <CheckCircleIcon sx={{ fontSize: 14, color: '#0F766E' }} />}
        {isUnassigned && <WarningAmberIcon sx={{ fontSize: 14, color: '#92400E' }} />}
      </Box>

      {/* Subject + class */}
      <Typography variant="body2" fontWeight="medium" noWrap sx={{ mb: 0.25 }}>
        {state.subjectName}
      </Typography>
      <Typography variant="caption" color="text.secondary" noWrap>
        {state.className}
      </Typography>

      {/* Result */}
      {isPending && (
        <Typography variant="caption" color="text.disabled" sx={{ mt: 0.75, display: 'block' }}>
          Waiting…
        </Typography>
      )}
      {isSearching && (
        <Typography variant="caption" color="primary.main" sx={{ mt: 0.75, display: 'block' }}>
          Searching candidates…
        </Typography>
      )}
      {isAssigned && state.assignedTeacher && state.assignedTeacher !== 'none' && (
        <Box sx={{ mt: 0.75 }}>
          <Typography variant="caption" fontWeight="bold" color="#0F766E" noWrap>
            {(state.assignedTeacher as { name: string }).name}
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
            {state.matchReasons.slice(0, 2).map((r) => (
              <Chip
                key={r}
                label={r}
                size="small"
                sx={{ height: 16, fontSize: 9, '& .MuiChip-label': { px: 0.75 } }}
                color="success"
                variant="outlined"
              />
            ))}
          </Box>
        </Box>
      )}
      {isUnassigned && (
        <Typography variant="caption" color="warning.dark" sx={{ mt: 0.75, display: 'block' }}>
          No eligible teacher found
        </Typography>
      )}
    </Box>
  );
}

// ─── Phase step indicator ─────────────────────────────────────────────────────

function PhaseStepBar({ currentPhase }: { currentPhase: AutoAssignPhase }) {
  const isSpecial = currentPhase === 'skipped' || currentPhase === 'error';
  const activeIdx = isSpecial ? -1 : phaseIndex(currentPhase);

  return (
    <Box sx={{ display: 'flex', gap: 0, mb: 2.5 }}>
      {PHASE_STEPS.map((step, idx) => {
        const isDone    = !isSpecial && activeIdx > idx;
        const isActive  = !isSpecial && activeIdx === idx;
        const isPending = isSpecial || activeIdx < idx;

        return (
          <Box
            key={step.phase}
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 0.5,
              position: 'relative',
              '&:not(:last-child)::after': {
                content: '""',
                position: 'absolute',
                top: 12,
                left: '50%',
                width: '100%',
                height: 2,
                bgcolor: isDone ? '#0F766E' : 'divider',
                transition: 'background-color 0.3s',
              },
            }}
          >
            <Box
              sx={{
                width: 24,
                height: 24,
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: isDone ? '#0F766E' : isActive ? 'primary.main' : 'action.hover',
                color: isDone || isActive ? '#fff' : 'text.disabled',
                transition: 'all 0.3s',
                zIndex: 1,
              }}
            >
              {isDone ? <CheckCircleIcon sx={{ fontSize: 14 }} /> : step.icon}
            </Box>
            <Typography
              variant="caption"
              sx={{
                fontSize: 10,
                fontWeight: isActive ? 700 : 400,
                color: isDone ? '#0F766E' : isActive ? 'primary.main' : 'text.disabled',
                textAlign: 'center',
                lineHeight: 1.2,
              }}
            >
              {step.label}
            </Typography>
          </Box>
        );
      })}
    </Box>
  );
}

// ─── Availability breakdown panel ─────────────────────────────────────────────

function AvailabilityPanel({ progress }: { progress: AutoAssignProgress }) {
  const av = progress.availability;
  if (!av) return null;

  const rows = [
    { label: 'Total teachers in school', value: av.totalTeachers, color: 'text.primary' },
    { label: 'Absent today', value: -av.absentCount, color: av.absentCount > 0 ? 'error.main' : 'text.secondary' },
    { label: 'Marked unavailable', value: -av.unavailableCount, color: av.unavailableCount > 0 ? 'warning.main' : 'text.secondary' },
    { label: 'Busy this period', value: -av.busyCount, color: av.busyCount > 0 ? 'warning.main' : 'text.secondary' },
    { label: 'Quota exceeded', value: -av.quotaExceededCount, color: av.quotaExceededCount > 0 ? 'warning.main' : 'text.secondary' },
    { label: 'Ineligible type', value: -av.ineligibleTypeCount, color: av.ineligibleTypeCount > 0 ? 'text.secondary' : 'text.secondary' },
  ].filter((r) => r.value !== 0);

  return (
    <Box sx={{ bgcolor: 'action.hover', borderRadius: 1.5, p: 1.5, mb: 2 }}>
      {rows.map((row) => (
        <Box key={row.label} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.25 }}>
          <Typography variant="caption" color="text.secondary">{row.label}</Typography>
          <Typography variant="caption" fontWeight="bold" color={row.color}>
            {row.value > 0 ? `${row.value}` : `${row.value}`}
          </Typography>
        </Box>
      ))}
      <Divider sx={{ my: 0.75 }} />
      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
        <Typography variant="caption" fontWeight="bold">Available for substitution</Typography>
        <Typography variant="caption" fontWeight="bold" color={av.availableCount > 0 ? '#0F766E' : 'error.main'}>
          {av.availableCount}
        </Typography>
      </Box>
    </Box>
  );
}

// ─── Summary dashboard ────────────────────────────────────────────────────────

function SummaryDashboard({ progress }: { progress: AutoAssignProgress }) {
  const allCovered = progress.periodsAssigned === progress.periodsFound && progress.periodsFound > 0;
  const uniqueTeachers = new Set(
    progress.periodStates
      .filter((s) => s.status === 'assigned' && s.assignedTeacher && s.assignedTeacher !== 'none')
      .map((s) => (s.assignedTeacher as { id: string }).id)
  ).size;

  return (
    <Box>
      {/* Headline */}
      <Box sx={{ textAlign: 'center', mb: 2 }}>
        {allCovered ? (
          <>
            <CheckCircleIcon sx={{ fontSize: 40, color: '#0F766E', mb: 0.5 }} />
            <Typography variant="h6" fontWeight="bold" color="#0F766E">
              Coverage plan complete!
            </Typography>
          </>
        ) : (
          <>
            <WarningAmberIcon sx={{ fontSize: 40, color: '#92400E', mb: 0.5 }} />
            <Typography variant="h6" fontWeight="bold" color="#92400E">
              Partial coverage — action needed
            </Typography>
          </>
        )}
      </Box>

      {/* Stats grid */}
      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5, mb: 2 }}>
        <StatCard
          value={`${progress.periodsAssigned}/${progress.periodsFound}`}
          label="Periods covered"
          color={allCovered ? '#0F766E' : '#0F766E'}
          bg="#F0FDFA"
        />
        <StatCard
          value={String(progress.periodsUnassigned)}
          label="Need attention"
          color={progress.periodsUnassigned > 0 ? '#92400E' : 'text.secondary'}
          bg={progress.periodsUnassigned > 0 ? '#FFFBEB' : 'action.hover'}
        />
        <StatCard
          value={String(uniqueTeachers)}
          label="Teachers involved"
          color="text.primary"
          bg="action.hover"
        />
        {progress.elapsedMs != null && (
          <StatCard
            value={`${(progress.elapsedMs / 1000).toFixed(1)}s`}
            label="Completed in"
            color="text.secondary"
            bg="action.hover"
          />
        )}
      </Box>

      {/* Unassigned periods callout */}
      {progress.periodsUnassigned > 0 && (
        <Alert severity="warning" sx={{ mb: 1.5, fontSize: 12 }}>
          {progress.periodsUnassigned} period{progress.periodsUnassigned !== 1 ? 's' : ''} could not be automatically assigned.
          Click the period cell in the grid to assign manually.
        </Alert>
      )}

      <Alert severity="info" sx={{ fontSize: 12 }}>
        Substitute teachers will be notified when you publish the plan.
      </Alert>
    </Box>
  );
}

function StatCard({ value, label, color, bg }: { value: string; label: string; color: string; bg: string }) {
  return (
    <Box sx={{ bgcolor: bg, borderRadius: 1.5, p: 1.5, textAlign: 'center' }}>
      <Typography variant="h5" fontWeight="bold" color={color}>{value}</Typography>
      <Typography variant="caption" color="text.secondary">{label}</Typography>
    </Box>
  );
}

// ─── Bulk queue panel ─────────────────────────────────────────────────────────

function BulkQueuePanel({ queue }: { queue: BulkQueueItem[] }) {
  return (
    <Box sx={{ mb: 2 }}>
      <Typography variant="caption" color="text.secondary" fontWeight="bold" sx={{ mb: 1, display: 'block' }}>
        Processing {queue.length} absence{queue.length !== 1 ? 's' : ''}
      </Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, maxHeight: 120, overflowY: 'auto' }}>
        {queue.map((item) => (
          <Box
            key={item.absenceId}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              px: 1,
              py: 0.5,
              borderRadius: 1,
              bgcolor: item.status === 'running' ? 'primary.light' : 'transparent',
              transition: 'background-color 0.2s',
            }}
          >
            <Avatar sx={{ width: 20, height: 20, fontSize: 9, bgcolor: item.status === 'done' ? '#0F766E' : item.status === 'error' ? 'error.main' : item.status === 'running' ? 'primary.main' : 'action.selected' }}>
              {item.status === 'done' ? '✓' : item.status === 'error' ? '!' : item.status === 'skipped' ? '–' : item.status === 'running' ? <CircularProgress size={10} color="inherit" /> : item.teacherName[0]}
            </Avatar>
            <Typography variant="caption" fontWeight={item.status === 'running' ? 700 : 400} noWrap sx={{ flex: 1 }}>
              {item.teacherName}
            </Typography>
            {item.status === 'done' && item.periodsTotal != null && (
              <Typography variant="caption" color="#0F766E">
                {item.periodsAssigned}/{item.periodsTotal}
              </Typography>
            )}
            {item.status === 'skipped' && (
              <Chip label="Skipped" size="small" sx={{ height: 16, fontSize: 9 }} />
            )}
            {item.status === 'error' && (
              <Chip label="Error" size="small" color="error" sx={{ height: 16, fontSize: 9 }} />
            )}
          </Box>
        ))}
      </Box>
      <Divider sx={{ mt: 1 }} />
    </Box>
  );
}

// ─── Main dialog ──────────────────────────────────────────────────────────────

export function AutoAssignProgressDialog({
  open,
  progress,
  bulkQueue,
  onClose,
  isRunning,
}: AutoAssignProgressDialogProps) {
  // Displayed progress — held for MIN_PHASE_MS to ensure legibility
  const [displayedProgress, setDisplayedProgress] = useState<AutoAssignProgress | null>(null);
  const lastPhaseRef = useRef<AutoAssignPhase | null>(null);
  const phaseTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Apply minimum phase display time
  useEffect(() => {
    if (!progress) {
      // progress reset to null = new teacher starting in bulk mode
      // Clear any pending timer and reset phase tracking so the next
      // teacher's first phase isn't skipped by the "same phase" fast-path
      if (phaseTimerRef.current) clearTimeout(phaseTimerRef.current);
      lastPhaseRef.current = null;
      setDisplayedProgress(null);
      return;
    }

    const incoming = progress.phase;

    // If same phase, always update immediately (per-period card updates within matching)
    if (incoming === lastPhaseRef.current) {
      setDisplayedProgress(progress);
      return;
    }

    // New phase — clear any pending timer and apply after MIN_PHASE_MS
    if (phaseTimerRef.current) clearTimeout(phaseTimerRef.current);

    phaseTimerRef.current = setTimeout(() => {
      setDisplayedProgress(progress);
      lastPhaseRef.current = incoming;
    }, MIN_PHASE_MS);

    return () => {
      if (phaseTimerRef.current) clearTimeout(phaseTimerRef.current);
    };
  }, [progress]);

  // Reset on open
  useEffect(() => {
    if (open) {
      setDisplayedProgress(null);
      lastPhaseRef.current = null;
    }
  }, [open]);

  const p = displayedProgress;
  const phase = p?.phase ?? 'discovery';
  const isComplete = phase === 'complete';
  const isSkipped = phase === 'skipped';
  const isError = phase === 'error';
  const isDone = isComplete || isSkipped || isError;

  // Overall progress bar value
  const progressValue = (() => {
    if (!p) return 0;
    if (isComplete) return 100;
    if (isSkipped || isError) return 100;
    const idx = phaseIndex(phase);
    if (idx < 0) return 0;
    // Each phase = 25%, within matching phase advance by period completion
    if (phase === 'matching' && p.periodsFound > 0) {
      const periodsDone = p.periodStates.filter((s) => s.status !== 'pending' && s.status !== 'searching').length;
      return 50 + (periodsDone / p.periodsFound) * 25;
    }
    return (idx / 4) * 100;
  })();

  return (
    <Dialog
      open={open}
      onClose={isDone && !isRunning ? onClose : undefined}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: 2 } }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {isRunning && !isDone ? (
            <CircularProgress size={18} color="primary" />
          ) : isComplete ? (
            <CheckCircleIcon sx={{ color: '#0F766E', fontSize: 20 }} />
          ) : isSkipped ? (
            <SkipNextIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
          ) : isError ? (
            <ErrorOutlineIcon sx={{ color: 'error.main', fontSize: 20 }} />
          ) : (
            <AutoFixHighIcon sx={{ color: 'primary.main', fontSize: 20 }} />
          )}
          <Typography variant="h6" fontWeight="bold">
            {isRunning && !isDone ? 'Auto-Assigning…' : isComplete ? 'Assignment Complete' : isSkipped ? 'Skipped' : isError ? 'Assignment Failed' : 'Auto-Assign'}
          </Typography>
        </Box>
        {/* Overall progress bar */}
        <LinearProgress
          variant={isRunning && !p && !isDone ? 'indeterminate' : 'determinate'}
          value={progressValue}
          sx={{
            mt: 1.5,
            height: 4,
            borderRadius: 2,
            bgcolor: 'action.hover',
            '& .MuiLinearProgress-bar': {
              bgcolor: isError ? 'error.main' : isSkipped ? 'text.secondary' : '#0F766E',
              transition: 'transform 0.4s ease',
            },
          }}
        />
      </DialogTitle>

      <DialogContent sx={{ pt: 1 }}>
        {/* Bulk queue */}
        {bulkQueue && bulkQueue.length > 1 && <BulkQueuePanel queue={bulkQueue} />}

        {/* Phase step bar — only for normal flow */}
        {!isSkipped && !isError && p && (
          <PhaseStepBar currentPhase={phase} />
        )}

        {/* Loading state before first callback */}
        {!p && isRunning && (
          <Box sx={{ textAlign: 'center', py: 3 }}>
            <CircularProgress size={32} />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
              Initialising…
            </Typography>
          </Box>
        )}

        {/* Phase content */}
        {p && (
          <Box>
            {/* Current message */}
            {!isComplete && !isSkipped && !isError && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2, minHeight: 20 }}>
                {p.message}
              </Typography>
            )}

            {/* Phase 1 — discovery */}
            {(phase === 'discovery') && p.periodsFound > 0 && (
              <Box sx={{ bgcolor: 'action.hover', borderRadius: 1.5, p: 1.5, mb: 2 }}>
                <Typography variant="body2" fontWeight="bold">
                  📅 {p.periodsFound} period{p.periodsFound !== 1 ? 's' : ''} found
                </Typography>
                {p.periodTimeRange && (
                  <Typography variant="caption" color="text.secondary">
                    ⏰ {p.periodTimeRange}
                  </Typography>
                )}
              </Box>
            )}

            {/* Phase 2 — availability */}
            {(phase === 'availability' || phase === 'matching' || phase === 'complete') && p.availability && (
              <Collapse in={phase === 'availability'}>
                <AvailabilityPanel progress={p} />
              </Collapse>
            )}

            {/* Phase 2 summary chip (shown during matching/complete) */}
            {(phase === 'matching' || phase === 'complete') && p.availability && (
              <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                <Chip
                  icon={<PersonIcon sx={{ fontSize: 14 }} />}
                  label={`${p.availability.availableCount} available`}
                  size="small"
                  color="success"
                  variant="outlined"
                  sx={{ height: 22, fontSize: 11 }}
                />
                {p.availability.absentCount > 0 && (
                  <Chip
                    label={`${p.availability.absentCount} absent`}
                    size="small"
                    color="error"
                    variant="outlined"
                    sx={{ height: 22, fontSize: 11 }}
                  />
                )}
                {p.availability.busyCount > 0 && (
                  <Chip
                    label={`${p.availability.busyCount} busy`}
                    size="small"
                    color="warning"
                    variant="outlined"
                    sx={{ height: 22, fontSize: 11 }}
                  />
                )}
                {p.availability.quotaExceededCount > 0 && (
                  <Chip
                    label={`${p.availability.quotaExceededCount} quota full`}
                    size="small"
                    variant="outlined"
                    sx={{ height: 22, fontSize: 11 }}
                  />
                )}
              </Box>
            )}

            {/* Phase 3 — per-period cards */}
            {(phase === 'matching' || phase === 'complete') && p.periodStates.length > 0 && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="caption" color="text.secondary" fontWeight="bold" sx={{ mb: 1, display: 'block' }}>
                  Period assignments
                </Typography>
                <Box sx={{ display: 'flex', gap: 1.5, overflowX: 'auto', pb: 1 }}>
                  {p.periodStates.map((state) => (
                    <PeriodCard key={state.periodId} state={state} />
                  ))}
                </Box>
              </Box>
            )}

            {/* Phase 4 — complete summary */}
            {isComplete && <SummaryDashboard progress={p} />}

            {/* Skipped state */}
            {isSkipped && (
              <Box sx={{ textAlign: 'center', py: 2 }}>
                <SkipNextIcon sx={{ fontSize: 40, color: 'text.secondary', mb: 1 }} />
                <Typography variant="body1" color="text.secondary">
                  {p.message}
                </Typography>
              </Box>
            )}

            {/* Error state */}
            {isError && (
              <Alert severity="error" sx={{ mt: 1 }}>
                {p.errorMessage || p.message}
              </Alert>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button
          variant={isDone ? 'contained' : 'outlined'}
          disabled={isRunning && !isDone}
          onClick={onClose}
          color={isError ? 'error' : 'primary'}
        >
          {isComplete ? 'Done' : isSkipped ? 'Close' : isError ? 'Dismiss' : 'Cancel'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
